import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import moment from 'moment/moment'

import { Button, Link, Separator, Text, Form } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getIsFetching } from '@/reducers/fetch'
import { POST_PAY_SCHEDULE_KEY, postPaySchedule } from '../payrollActions'
import { generateFilterAnchorDate, generateFilterFirstDate } from '../helpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Gusto_PayScheduleFrequency } from '../generated_gusto_types'
import { TypographyStyles } from '@/styles'
import {
  makeReqStringSchema,
  makeDateSchema,
  getFieldName,
} from '@/componentsV1/BaseComponents'

const FREQUENCY_OPTIONS = [
  { text: 'Every week', value: 'Every week' },
  { text: 'Every other week', value: 'Every other week' },
  {
    text: 'Twice per month: 15th and last day of the month',
    value: 'Twice per month',
  },
  { text: 'Monthly: last day of the month', value: 'Monthly' },
]

export const SchedulePreview = ({
  frequency,
  firstPayDate,
}: {
  frequency: string
  firstPayDate: string
}) => {
  const date = moment(firstPayDate)

  return (
    <div className="pay-schedule-preview flex flex-col gap-4">
      <Text variant={TypographyStyles.BODY_SM}>YOUR SELECTED PAY SCHEDULE</Text>

      <Separator />

      <div className="flex flex-col">
        <div className="w-full md:w-1/2 mb-4">
          <Text>Pay Frequency</Text>
          <Text variant={TypographyStyles.H2}>{frequency}</Text>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-1/2">
          <Text>First Pay Date</Text>
          <Text variant={TypographyStyles.H2}>
            {date.isValid() ? date.format(DATE_FORMATS.DISPLAY_LONG) : ''}
          </Text>
        </div>
        <div className="w-full md:w-1/2">
          <Text>Deadline to Run Payroll</Text>
          <Text variant={TypographyStyles.H2}>
            {date.isValid()
              ? date.subtract(4, 'days').format(DATE_FORMATS.DISPLAY_LONG)
              : ''}
          </Text>
        </div>
      </div>
    </div>
  )
}

const validationSchema = yup.object({
  frequency: makeReqStringSchema({ field: 'frequency' }),
  firstPayDate: makeDateSchema({ field: 'first pay date' }),
  anchorEndDate: makeDateSchema({ field: 'anchor end of pay period' }),
})

export const PayrollScheduleForm = ({
  onSuccess,
  onCancel,
  saveText,
}: {
  onSuccess: () => void
  onCancel: () => void
  saveText: string
}) => {
  const dispatch = useAppDispatch()
  const isPosting = useReselector(getIsFetching, POST_PAY_SCHEDULE_KEY)

  const formik = useFormik<{
    frequency: Gusto_PayScheduleFrequency
    firstPayDate: string
    anchorEndDate: string
  }>({
    initialValues: {
      frequency: 'Every other week',
      firstPayDate: '',
      anchorEndDate: '',
    },
    validationSchema,
    onSubmit: async ({ frequency, firstPayDate, anchorEndDate }) => {
      let day_1 = null

      if (frequency === 'Twice per month') {
        day_1 = 15
      } else if (frequency === 'Monthly') {
        day_1 = 31
      }

      const res = await postPaySchedule({
        frequency,
        anchor_pay_date: moment(firstPayDate, DATE_FORMATS.INPUT).format(
          DATE_FORMATS.GUSTO_SUBMIT
        ),
        anchor_end_of_pay_period: moment(
          anchorEndDate,
          DATE_FORMATS.INPUT
        ).format(DATE_FORMATS.GUSTO_SUBMIT),
        day_1,
        day_2: frequency === 'Twice per month' ? 31 : null,
      })(dispatch)

      if (res) {
        onSuccess()
      }
    },
  })

  const filterFirstDate = useMemo(
    () => generateFilterFirstDate(formik.values.frequency),
    [formik.values.frequency]
  )
  const filterAnchorDate = useMemo(
    () =>
      generateFilterAnchorDate(
        formik.values.firstPayDate,
        formik.values.frequency
      ),
    [formik.values.firstPayDate, formik.values.frequency]
  )

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-6">
        <Text variant={TypographyStyles.H2}>Create a Pay Schedule</Text>

        <Text variant={TypographyStyles.BODY_MD}>
          Pick the frequency at which you would like to run payroll.{' '}
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/7257879526807"
            target="_blank"
            rel="noopener noreferrer"
          >
            How to choose a pay schedule.
          </Link>
        </Text>

        <Text variant={TypographyStyles.BODY_MD}>
          Some states have laws around when you must pay your employees. Please
          choose a pay schedule that is legal for your employees.
        </Text>

        <Separator />

        <div className="w-full max-w-[640px]">
          <Form.Select
            name="frequency"
            options={FREQUENCY_OPTIONS}
            label="Pay Frequency"
            description="We recommend paying yourself once per month, on the last day of each month"
            onChange={() => {
              formik.setFieldValue('firstPayDate', '')
              formik.setFieldValue('anchorEndDate', '')
            }}
            required
          />
        </div>

        <div className="w-full max-w-[640px]">
          <Form.DateInput
            name={getFieldName<typeof formik.values>('firstPayDate')}
            label="First Pay Date"
            description="When selecting your pay date, please account for the 4 days it will take to process payroll."
            required
            filterDate={filterFirstDate}
            onValueChange={() => formik.setFieldValue('anchorEndDate', '')}
          />
        </div>

        <div className="w-full max-w-[640px]">
          <Form.DateInput
            name={getFieldName<typeof formik.values>('anchorEndDate')}
            label="Anchor End Date"
            description="Last day of the first pay period. Can be the same as first paydate"
            fullWidth
            required
            filterDate={filterAnchorDate}
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-4 mt-2">
          <Button variant="secondary" onClick={onCancel} className="sm:w-1/2">
            Back
          </Button>
          <div className="hidden sm:block sm:w-[16.666%]" />{' '}
          {/* Spacer for layout alignment */}
          <Button
            onClick={formik.submitForm}
            loading={isPosting}
            className="sm:w-1/2"
          >
            {saveText}
          </Button>
        </div>
      </div>
    </FormikProvider>
  )
}
